import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { AppSettingsService } from "@/service/AppSettingsService";
import Logger from "@/utils/Logger";
import store from "@/store";
import {
  selectAccessToken,
  selectIsAuthenticated,
} from "@/store/selectors/authSelectors";
import { logDiagnostic } from "@/utils/LogHelper";

// Determine if the application is running on localhost
const isLocalhost = window.location.hostname.includes("localhost");

// Check if we should use internal API (requires connection error handling)
const shouldUseInternalApi = (): boolean => {
  try {
    return AppSettingsService.shouldUseInternalApi();
  } catch (error) {
    Logger.warn(
      "Failed to determine API mode, defaulting to external (no connection error handling)",
      ["ApiClient"],
    );
    return false;
  }
};

// Configuration constants
const API_CONFIG = {
  BASE_URL: AppSettingsService.getApiUrl(),
  HEADERS: {
    CACHE_CONTROL: "no-cache",
    PRAGMA: "no-cache",
    CONTENT_TYPE: "application/json",
    CLIENT_IDENTIFIER: AppSettingsService.getApiClientIdentifier(),
  },
  //TODO UserStory: 446368 - Increased timeout to 20 seconds to accommodate
  // longer-running requests, especially in high-latency environments.
  // This is a temporary change and may be adjusted
  // based on further performance testing and user feedback.
  TIMEOUT: 20000,
};

/**
 * Get access token from Redux store (safe for interceptors - no hooks)
 */
const getAccessToken = (): string | null => {
  try {
    const state = store.getState();
    const token = selectAccessToken(state);
    const isAuthenticated = selectIsAuthenticated(state);

    if (token && isAuthenticated) {
      return token;
    }
    return null;
  } catch (error) {
    Logger.error("Error getting access token from store", error, ["ApiClient"]);
    return null;
  }
};

/**
 * Check if the URL is a logging endpoint (telemetry or diagnostic)
 * These should fail silently without redirecting to prevent infinite loops
 */
const isLoggingEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return url.includes("/log/telemetry") || url.includes("/log/diagnostic");
};

/**
 * Check if we're already on the connection error page
 * Prevents multiple redirects and infinite loops
 */
const isOnConnectionErrorPage = (): boolean => {
  return window.location.pathname.includes("/connection-error");
};

/**
 * Track redirect attempts to prevent rapid-fire redirects
 * This provides additional protection against infinite redirect loops
 */
let lastRedirectTime = 0;
const REDIRECT_COOLDOWN_MS = 3000; // 3 seconds between redirects

/**
 * Reset redirect cooldown timer - FOR TESTING ONLY
 * This allows tests to verify redirect behavior without waiting for cooldown
 */
export const resetRedirectCooldown = (): void => {
  lastRedirectTime = 0;
};

/**
 * Safe redirect to connection error page with cooldown
 * ONLY used when use_internal_api_url is true (internal AKS API)
 */
const redirectToConnectionError = (context: string): void => {
  const now = Date.now();

  // CRITICAL: Only redirect if using internal API
  if (!shouldUseInternalApi()) {
    Logger.debug(
      `Skipping connection error redirect (external API mode) from ${context}`,
      ["ApiClient"],
    );
    return;
  }

  // Check if we're already on the error page
  if (isOnConnectionErrorPage()) {
    Logger.debug(
      `Already on connection-error page, skipping redirect from ${context}`,
      ["ApiClient"],
    );
    return;
  }

  // Check cooldown to prevent rapid redirects
  if (now - lastRedirectTime < REDIRECT_COOLDOWN_MS) {
    Logger.debug(
      `Redirect cooldown active, skipping redirect from ${context}`,
      ["ApiClient"],
    );
    return;
  }

  lastRedirectTime = now;
  Logger.error(`Redirecting to connection-error page from ${context}`, null, [
    "ApiClient",
  ]);
  window.location.replace("/connection-error");
};

/**
 * Network error handler with improved safety checks
 * Respects use_internal_api_url flag for connection error handling
 */
const handleNetworkError = (error: AxiosError, context: string): void => {
  // CRITICAL: Logging endpoints must fail silently to prevent infinite loops
  if (isLoggingEndpoint(error.config?.url)) {
    Logger.warn(
      `Logging endpoint ${error.config?.url} failed silently - backend may be unavailable or CORS misconfigured`,
      ["ApiClient"],
      { code: error.code, status: error.response?.status },
    );
    return;
  }

  // CRITICAL: Only redirect for network errors when using INTERNAL API
  // External API Gateway should always be accessible, so no redirect needed
  if (axios.isAxiosError(error) && error.code === "ERR_NETWORK") {
    if (shouldUseInternalApi()) {
      Logger.error(
        `Network error in ${context} (internal API mode), redirecting to connection-error page`,
        error,
        ["ApiClient"],
      );
      redirectToConnectionError(context);
    } else {
      Logger.error(
        `Network error in ${context} (external API mode), NOT redirecting`,
        error,
        ["ApiClient"],
      );
    }
  } else {
    Logger.error(`API error in ${context}`, error, ["ApiClient"]);
  }
};

/**
 * Check if error should trigger network error handling
 */
const isNetworkError = (error: unknown): error is AxiosError => {
  return (
    axios.isAxiosError(error) &&
    (error.code === "ERR_NETWORK" ||
      error.code === "NETWORK_ERROR" ||
      error.message === "Network Error" ||
      !error.response) // No response usually means network issue
  );
};

// Create an Axios instance with default configuration
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    "Cache-Control": API_CONFIG.HEADERS.CACHE_CONTROL,
    Pragma: API_CONFIG.HEADERS.PRAGMA,
    "Content-Type": API_CONFIG.HEADERS.CONTENT_TYPE,
    "Client-Identifier": API_CONFIG.HEADERS.CLIENT_IDENTIFIER,
  },
  timeout: API_CONFIG.TIMEOUT,
});

// UNIFIED REQUEST INTERCEPTOR
api.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    // Get fresh token for each request
    const token = getAccessToken();

    // Only add Bearer token for external API calls
    if (request.baseURL !== window.location.origin && token) {
      request.headers.Authorization = `Bearer ${token}`;
      Logger.debug("Bearer token attached to request", ["ApiClient"]);
    } else if (!token && request.baseURL !== window.location.origin) {
      Logger.warn("No access token available for external request", [
        "ApiClient",
      ]);
    }

    Logger.info(
      `[API Request] ${request.method?.toUpperCase()} ${request.url}`,
      ["ApiClient"],
    );
    return request;
  },
  (error: AxiosError) => {
    Logger.error("[API Request Error]", error.message, ["ApiClient"]);

    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, "Request Interceptor");
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    );
  },
);

// UNIFIED RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response: AxiosResponse) => {
    Logger.info(`[API Response] ${response.status} ${response.config.url}`, [
      "ApiClient",
    ]);
    return response;
  },
  async (error: unknown) => {
    const requestUrl = axios.isAxiosError(error)
      ? error.config?.url || "unknown"
      : "unknown";

    // CRITICAL: Check if this is a logging endpoint FIRST
    if (axios.isAxiosError(error) && isLoggingEndpoint(error.config?.url)) {
      // Log to console only, do NOT redirect or throw
      console.warn(
        `[ApiClient] Logging API failure (${error.code || error.response?.status}):`,
        requestUrl,
        "- This is expected if backend logging is unavailable",
      );
      // Return rejected promise but don't trigger redirects
      return Promise.reject(error);
    }

    // Handle network errors for NON-logging endpoints
    // ONLY redirect when use_internal_api_url is true
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `Response from ${requestUrl}`);
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }

    // Handle 401 unauthorized
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      Logger.error("Received 401 Unauthorized - token may be expired", error, [
        "ApiClient",
      ]);
    }

    // Enhanced error logging for other errors
    if (axios.isAxiosError(error)) {
      const _error: AxiosError = error;
      const status = _error.response?.status ?? "Unknown";
      const url =
        _error.response?.config?.url ?? _error.config?.url ?? "unknown";

      if (_error.response) {
        Logger.error(
          `[API Response Error] ${status} ${url}`,
          _error.response.data,
          ["ApiClient"],
        );
      } else if (_error.request) {
        Logger.error("[API No Response]", _error.request, ["ApiClient"]);
      } else {
        Logger.error("[API Setup Error]", error.message, ["ApiClient"]);
      }

      // ONLY log to diagnostics for non-logging endpoints
      if (!isLoggingEndpoint(_error.config?.url)) {
        const tags = ["NetworkError", "ApiClient"];
        logDiagnostic(error, tags?.join(",") || "Unknown Context").catch(
          (logError: unknown) => {
            // Silent catch - don't log failures of logging
            console.warn("[Diagnostic Log Failed]", logError);
          },
        );
      }
    } else {
      Logger.error("[Non-Axios Error]", error, ["ApiClient"]);
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    );
  },
);

// CRUD utility functions with improved error handling
export const apiGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.get<T>(url, config);
    return response.data;
  } catch (error) {
    // Don't log GET errors for logging endpoints
    if (!isLoggingEndpoint(url)) {
      Logger.error(`[GET Error] ${url}`, error, ["ApiClient"]);
    }

    // Handle network errors in GET requests (non-logging endpoints)
    if (isNetworkError(error) && !isLocalhost && !isLoggingEndpoint(url)) {
      handleNetworkError(error, `GET ${url}`);
    }

    throw error;
  }
};

export const apiPost = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.post<T>(url, data, config);
    return response.data;
  } catch (error) {
    // Don't log POST errors for logging endpoints
    if (!isLoggingEndpoint(url)) {
      Logger.error(`[POST Error] ${url}`, error, ["ApiClient"]);
    }

    // Handle network errors in POST requests (non-logging endpoints)
    if (isNetworkError(error) && !isLocalhost && !isLoggingEndpoint(url)) {
      handleNetworkError(error, `POST ${url}`);
    }

    throw error;
  }
};

export const apiPut = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.put<T>(url, data, config);
    return response.data;
  } catch (error) {
    Logger.error(`[PUT Error] ${url}`, error, ["ApiClient"]);

    // Handle network errors in PUT requests
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `PUT ${url}`);
    }

    throw error;
  }
};

export const apiDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const response = await api.delete<T>(url, config);
    return response.data;
  } catch (error) {
    Logger.error(`[DELETE Error] ${url}`, error, ["ApiClient"]);

    // Handle network errors in DELETE requests
    if (isNetworkError(error) && !isLocalhost) {
      handleNetworkError(error, `DELETE ${url}`);
    }

    throw error;
  }
};

// Export utility functions for manual error handling in components
export const handleApiError = (error: unknown, context: string): void => {
  if (isNetworkError(error) && !isLocalhost) {
    handleNetworkError(error, context);
  } else {
    Logger.error(`API error in ${context}`, error, ["ApiClient"]);
  }
};

export const isApiNetworkError = (error: unknown): error is AxiosError => {
  return isNetworkError(error);
};

// Additional utility functions for better error handling
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (
      error.response?.data &&
      typeof error.response.data === "object" &&
      "message" in error.response.data
    ) {
      const data = error.response.data as { message: unknown };
      if (typeof data.message === "string") {
        return data.message;
      }
    }
    if (error.message) {
      return error.message;
    }
    return `HTTP ${error.response?.status || "Unknown"} Error`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown error occurred";
};

export const getErrorStatusCode = (error: unknown): number | null => {
  if (axios.isAxiosError(error)) {
    return error.response?.status || null;
  }
  return null;
};

export default api;
